import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import Decimal from 'decimal.js';
import type { PontoEquilibrioResponse } from '@triade/contracts';
import { usePontoEquilibrio, type JanelaPontoEquilibrio } from '@/api/ponto-equilibrio';
import { usePode } from '@/api/me';
import { SeloFaixaMc } from '@/components/margem-contribuicao';
import { formatarCompetencia } from '@/components/inicio/tipos';
import { ehDecimal, formatarData, formatarFracaoPercentual, formatarMoeda } from '@/lib/format';
import { cn } from '@/lib/utils';
import { MostradorPe } from './mostrador-pe';

const JANELAS: JanelaPontoEquilibrio[] = [1, 3, 6, 12];
const ROTULO_BASE_APROVADOS = 'Base: vendas aprovadas no período';

/** Posicao (fracao 0..1 em CSS) de um valor na escala da barra; so geometria. */
function posicaoNaEscala(valor: string, escala: Decimal): string {
  const f = new Decimal(valor).div(escala).toNumber();
  return `${Math.min(Math.max(f, 0), 1) * 100}%`;
}

/**
 * Ponto de equilibrio da competencia do ultimo rateio FECHADO (contabil, economico, financeiro). Tudo vem da API;
 * a tela so formata e desenha a barra (largura = receita / max(receita, PE economico), so geometria).
 * Sem custo.ver ou rateio.ver nao aparece e nao chama a API.
 */
export function PontoEquilibrioCard({ largura = 'compacta' }: { largura?: LarguraPe }) {
  const { pode, carregando } = usePode();
  // A consulta so existe (e so e chamada) para quem tem as duas permissoes; o componente interno so monta com elas.
  if (carregando || !pode('custo.ver') || !pode('rateio.ver')) return null;
  return <PontoEquilibrioConsulta largura={largura} />;
}

/** Largura do mostrador: `compacta` (Inicio) ou `ampla` (Painel de margens). */
export type LarguraPe = 'compacta' | 'ampla';
const MAX_LARGURA_MOSTRADOR: Record<LarguraPe, string> = { compacta: 'max-w-[360px]', ampla: 'max-w-[520px]' };

function PontoEquilibrioConsulta({ largura }: { largura: LarguraPe }) {
  const [meses, setMeses] = useState<JanelaPontoEquilibrio>(3);
  const consulta = usePontoEquilibrio(meses, true);
  const d = consulta.data;

  return (
    <section aria-labelledby="pe-titulo" data-testid="ponto-equilibrio" className="flex flex-col gap-4 rounded-2xl border border-border bg-card p-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 id="pe-titulo" className="text-title font-semibold">
            Ponto de equilíbrio
          </h2>
          {d?.competencia && <p className="mt-0.5 text-small text-ink-muted">Competência {formatarCompetencia(d.competencia)}</p>}
        </div>
        <div role="group" aria-label="Janela da MC média" className="inline-flex rounded-lg border border-border p-0.5">
          {JANELAS.map((n) => (
            <button
              key={n}
              type="button"
              aria-pressed={meses === n}
              onClick={() => setMeses(n)}
              className={cn('rounded-md px-3 py-1 text-small font-medium', meses === n ? 'bg-primary text-primary-foreground' : 'text-ink-secondary hover:bg-line-soft')}
            >
              {n} {n === 1 ? 'mês' : 'meses'}
            </button>
          ))}
        </div>
      </div>

      {consulta.isError && !d ? (
        <p role="alert" className="text-small text-danger-ink">
          Não foi possível calcular o ponto de equilíbrio.
        </p>
      ) : !d ? (
        <p role="status" className="text-small text-ink-muted">
          Calculando…
        </p>
      ) : d.status !== 'OK' ? (
        <Indisponivel d={d} janela={meses} onJanela={setMeses} />
      ) : (
        <Conteudo d={d} maxLargura={MAX_LARGURA_MOSTRADOR[largura]} />
      )}
    </section>
  );
}

/** Aviso discreto: aprovados que nao entram na MC% por falta do custo separado no snapshot. */
function AvisoForaDoCalculo({ quantos }: { quantos: number }) {
  if (quantos <= 0) return null;
  const sujeito = quantos === 1 ? '1 orçamento aprovado ficou' : `${quantos} orçamentos aprovados ficaram`;
  return (
    <p role="status" data-testid="pe-fora-do-calculo" className="text-caption text-ink-muted">
      {`${sujeito} de fora: sem o custo separado (materiais/aquisição × processo) no snapshot.`}
    </p>
  );
}

function Indisponivel({ d, janela, onJanela }: { d: PontoEquilibrioResponse; janela: JanelaPontoEquilibrio; onJanela: (m: JanelaPontoEquilibrio) => void }) {
  const alt = d.alternativa;
  return (
    <div className="flex flex-col gap-3">
      <div role="status" className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border bg-line-soft px-4 py-3 text-small text-ink-body">
        <span>{d.mensagem ?? 'Ponto de equilíbrio indisponível.'}</span>
        {d.link && (
          <Link to={d.link.rota as '/custos/rateios'} search={d.link.busca as { comp?: string }} className="font-medium text-primary hover:underline">
            Abrir rateios
          </Link>
        )}
      </div>
      {alt && (
        <div className="flex flex-wrap items-center justify-between gap-3 text-small text-ink-body" data-testid="pe-alternativa">
          <span>
            {`Há ${alt.aprovados} ${alt.aprovados === 1 ? 'orçamento aprovado' : 'orçamentos aprovados'} nos últimos 12 meses (MC% ponderada pela receita: ${formatarFracaoPercentual(alt.mcPct)})`}
          </span>
          {janela !== 12 && (
            <button type="button" onClick={() => onJanela(12)} className="font-medium text-primary hover:underline">
              Usar os últimos 12 meses
            </button>
          )}
        </div>
      )}
      <AvisoForaDoCalculo quantos={d.aprovados.foraDoCalculo} />
    </div>
  );
}

/** Base da MC% usada: aprovados (texto fixo) ou carteira/lista (texto pronto da API). */
function BaseMc({ d }: { d: PontoEquilibrioResponse }) {
  if (d.mc.origem === 'APROVADOS') {
    return (
      <span className="block text-caption font-normal text-ink-muted" data-testid="base-pe">
        {ROTULO_BASE_APROVADOS}
      </span>
    );
  }
  if (d.mc.origem === null || !d.baseDescricao) return null;
  return (
    <span className="block text-caption font-normal text-ink-muted" data-testid="base-pe">
      {d.baseDescricao}
    </span>
  );
}

function Conteudo({ d, maxLargura }: { d: PontoEquilibrioResponse; maxLargura: string }) {
  const pe = d.pe;
  const receita = ehDecimal(d.receitaMes) ? d.receitaMes : null;
  // Escala so da barra: receita / max(receita, PE economico). Marcas usam a mesma escala e ficam limitadas a 0..100%.
  const escala = receita !== null && pe && new Decimal(Decimal.max(receita, pe.economico)).gt(0) ? Decimal.max(receita, pe.economico) : null;
  const segurancaNegativa = ehDecimal(d.margemSeguranca) && new Decimal(d.margemSeguranca).lt(0);
  const marcas = pe && escala
    ? [
        { rotulo: 'PE contábil', valor: pe.contabil, cor: 'bg-ink-muted' },
        { rotulo: 'PE econômico', valor: pe.economico, cor: 'bg-primary' },
        { rotulo: 'PE financeiro', valor: pe.financeiro, cor: 'bg-warning-dot' },
      ]
    : [];

  return (
    <div className="flex flex-col gap-4 text-small">
      <dl className="grid grid-cols-[repeat(auto-fit,minmax(180px,1fr))] gap-x-6 gap-y-3">
        <div>
          <dt className="text-caption text-ink-muted">Gastos fixos</dt>
          <dd className="tabular font-medium">{formatarMoeda(d.fixos)}</dd>
        </div>
        <div>
          <dt className="text-caption text-ink-muted">Depreciação nos fixos</dt>
          <dd className="tabular font-medium">{formatarMoeda(d.depreciacao)}</dd>
        </div>
        <div>
          <dt className="text-caption text-ink-muted">MC% média ({d.meses} {d.meses === 1 ? 'mês' : 'meses'})</dt>
          <dd className="font-medium">
            <span className="tabular">{formatarFracaoPercentual(d.mc.pct)}</span>
            <BaseMc d={d} />
            <span className="block text-caption font-normal text-ink-muted" data-testid="pe-aprovados">
              {`${d.aprovados.usados} ${d.aprovados.usados === 1 ? 'aprovado usado' : 'aprovados usados'}${
                d.aprovados.deSnapshot > 0 ? ` (${d.aprovados.deSnapshot} calculados a partir do snapshot da versão)` : ''
              }`}
            </span>
          </dd>
        </div>
        <div>
          <dt className="text-caption text-ink-muted">Receita aprovada do mês</dt>
          <dd className="tabular font-medium">{formatarMoeda(d.receitaMes)}</dd>
        </div>
      </dl>

      {d.gauge && (
        <MostradorPe gauge={d.gauge} className={maxLargura} />
      )}

      {!d.gauge && escala && receita !== null && (
        <div className="flex flex-col gap-2" data-testid="pe-barra">
          <div className="flex items-baseline justify-between text-caption text-ink-muted">
            <span>Receita aprovada do mês × PE</span>
            <span>
              Margem de segurança{' '}
              <b data-testid="pe-margem-seguranca" className={cn('tabular', segurancaNegativa ? 'text-danger-ink' : 'text-ink')}>
                {formatarFracaoPercentual(d.margemSeguranca)}
              </b>
            </span>
          </div>
          <div className="relative h-3 rounded-md bg-line-soft" role="img" aria-label={`Receita ${formatarMoeda(d.receitaMes)} frente aos pontos de equilíbrio`}>
            <div className="absolute inset-y-0 left-0 rounded-md bg-success-bg" style={{ width: posicaoNaEscala(receita, escala) }} />
            {marcas.map((m) => (
              <div key={m.rotulo} aria-hidden="true" className={cn('absolute -top-[3px] -bottom-[3px] w-0.5', m.cor)} style={{ left: posicaoNaEscala(m.valor, escala) }} />
            ))}
          </div>
        </div>
      )}

      {pe && (
        <dl className="grid grid-cols-[repeat(auto-fit,minmax(160px,1fr))] gap-x-6 gap-y-2" data-testid="pe-valores">
          <div>
            <dt className="text-caption text-ink-muted">PE contábil</dt>
            <dd className="tabular font-semibold" data-testid="pe-contabil">{formatarMoeda(pe.contabil)}</dd>
          </div>
          <div>
            <dt className="text-caption text-ink-muted">PE econômico</dt>
            <dd className="tabular font-semibold" data-testid="pe-economico">{formatarMoeda(pe.economico)}</dd>
          </div>
          <div>
            <dt className="text-caption text-ink-muted">PE financeiro</dt>
            <dd className="tabular font-semibold" data-testid="pe-financeiro">{formatarMoeda(pe.financeiro)}</dd>
          </div>
        </dl>
      )}

      <div className="flex flex-wrap items-center justify-between gap-2 border-t border-line-soft pt-3" data-testid="pe-despesa-fixa">
        <span className="text-ink-secondary">
          Despesa fixa real × parâmetro:{' '}
          <b className="tabular">{formatarFracaoPercentual(d.despesaFixa.real)}</b> × <span className="tabular">{formatarFracaoPercentual(d.despesaFixa.parametro)}</span>
          {d.despesaFixa.diferenca !== null && <> (diferença <span className="tabular">{formatarFracaoPercentual(d.despesaFixa.diferenca)}</span>)</>}
        </span>
        {d.despesaFixa.alerta && (
          <span data-testid="pe-alerta-despesa-fixa">
            <SeloFaixaMc faixa="RED" rotulo="Despesa fixa acima do parâmetro" />
          </span>
        )}
      </div>
      {d.periodo && (
        <p className="text-caption text-ink-muted">
          Janela de {formatarData(d.periodo.de)} a {formatarData(d.periodo.ate)}.
        </p>
      )}
      <AvisoForaDoCalculo quantos={d.aprovados.foraDoCalculo} />
    </div>
  );
}
